import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  ReactFlow,
  addEdge,
  useNodesState,
  useEdgesState,
  Controls,
  MiniMap,
  Background,
  BackgroundVariant,
  MarkerType,
} from '@xyflow/react';
import AssetNode from '../nodes/AssetNode';
import { CATEGORIES } from '../store/assetDefinitions';

const API = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;

const nodeTypes = { asset: AssetNode };

const defaultEdgeOptions = {
  type: 'smoothstep',
  animated: true,
  style: { stroke: '#4ecca3', strokeWidth: 2 },
  markerEnd: { type: MarkerType.ArrowClosed, color: '#4ecca3' },
};

// SVG icons matching CENEC sidebar style
const ASSET_ICONS = {
  solar: (color) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/>
      <line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  ),
  wind: (color) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="14" r="2"/><line x1="12" y1="12" x2="12" y2="2"/>
      <path d="M12 2 L8 8"/><path d="M12 2 L16 8"/><path d="M12 14 L8 22"/><path d="M12 14 L16 22"/>
    </svg>
  ),
  battery: (color) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="1" y="6" width="18" height="12" rx="2"/><line x1="23" y1="10" x2="23" y2="14"/>
      <line x1="7" y1="10" x2="7" y2="14"/><line x1="12" y1="10" x2="12" y2="14"/>
    </svg>
  ),
  default: (color) => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2"/><line x1="12" y1="8" x2="12" y2="16"/>
      <line x1="8" y1="12" x2="16" y2="12"/>
    </svg>
  ),
};

const getAssetIcon = (defId, color) => {
  const fn = ASSET_ICONS[defId] || ASSET_ICONS.default;
  return fn(color || '#94a3b8');
};

let nodeId = 1;

const CanvasViewer = ({ context, onBack }) => {
  const { projectId, projectName, versionName, revisionId, revLetter } = context;

  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selectedNodeId, setSelectedNodeId] = useState(null);
  const [reactFlowInstance, setReactFlowInstance] = useState(null);
  const loadedRef = useRef(false);
  const saveTimerRef = useRef(null);
  const [projectAssets, setProjectAssets] = useState([]);

  useEffect(() => {
    fetch(`${API}/api/projects/${projectId}/assets`)
      .then((r) => r.ok ? r.json() : [])
      .then((list) => setProjectAssets(list || []))
      .catch(() => {});
  }, [projectId]);

  // Load canvas and hydrate nodes from current project_assets data
  useEffect(() => {
    if (loadedRef.current) return;
    loadedRef.current = true;

    Promise.all([
      fetch(`${API}/api/revisions/${revisionId}/canvas`).then((r) => r.ok ? r.json() : { nodes: [], edges: [] }),
      fetch(`${API}/api/projects/${projectId}/assets`).then((r) => r.ok ? r.json() : []),
    ]).then(([canvasData, assets]) => {
      setProjectAssets(assets || []);

      if (canvasData.nodes && canvasData.nodes.length > 0) {
        // Build a lookup by projectAssetId
        const assetMap = {};
        (assets || []).forEach((a) => { assetMap[a.id] = a; });

        // Hydrate each canvas node with current DB values
        const hydrated = canvasData.nodes.map((n) => {
          const pa = assetMap[n.data?.projectAssetId];
          if (!pa) return n; // orphan node — keep as-is
          const catMeta = CATEGORIES.find((c) => c.id === pa.category);
          return {
            ...n,
            data: {
              ...n.data,
              label: pa.name,
              icon: pa.icon || n.data.icon,
              color: catMeta?.color || pa.color || n.data.color,
              properties: pa.config || n.data.properties,
              connectors: (pa.config && pa.config.connectors) || pa.connectors || n.data.connectors,
            },
          };
        });
        setNodes(hydrated);
        nodeId = Math.max(...hydrated.map((n) => parseInt(n.id, 10) || 0)) + 1;
      }
      if (canvasData.edges) setEdges(canvasData.edges);
    }).catch(() => {});
  }, [revisionId, projectId, setNodes, setEdges]);

  const saveCanvas = useCallback((currentNodes, currentEdges) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      fetch(`${API}/api/revisions/${revisionId}/canvas`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ nodes: currentNodes, edges: currentEdges }),
      }).catch(() => {});
    }, 800);
  }, [revisionId]);

  useEffect(() => {
    if (loadedRef.current && (nodes.length > 0 || edges.length > 0)) {
      saveCanvas(nodes, edges);
    }
  }, [nodes, edges, saveCanvas]);

  const onConnect = useCallback(
    (params) => setEdges((eds) => addEdge(params, eds)),
    [setEdges]
  );

  const onDragOver = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const onDrop = useCallback(
    (event) => {
      event.preventDefault();
      const data = event.dataTransfer.getData('application/reactflow');
      if (!data || !reactFlowInstance) return;
      const pa = JSON.parse(data);
      const position = reactFlowInstance.screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });
      const catMeta = CATEGORIES.find((c) => c.id === pa.category);
      const newNode = {
        id: String(nodeId++),
        type: 'asset',
        position,
        data: {
          label: pa.name,
          icon: pa.icon || '⚡',
          color: catMeta?.color || pa.color || '#4ecca3',
          assetType: pa.asset_def_id,
          projectAssetId: pa.id,
          status: 'active',
          properties: pa.config || {},
          connectors: (pa.config && pa.config.connectors) || pa.connectors || null,
        },
      };
      setNodes((nds) => [...nds, newNode]);
    },
    [reactFlowInstance, setNodes]
  );

  const onNodeClick = useCallback((_, node) => setSelectedNodeId(node.id), []);
  const onPaneClick = useCallback(() => setSelectedNodeId(null), []);
  const onNodeDoubleClick = useCallback((_, node) => {
    alert(`Configure: ${node.data.label}\n\nProject asset ID: ${node.data.projectAssetId}\n\nFull inline config panel coming next.`);
  }, []);

  const groupedAssets = {};
  projectAssets.forEach((a) => {
    (groupedAssets[a.category] = groupedAssets[a.category] || []).push(a);
  });

  const canvasItems = nodes.map((n) => ({
    id: n.id,
    label: n.data.label,
    icon: n.data.icon,
    color: n.data.color,
    assetType: n.data.assetType,
    selected: n.id === selectedNodeId,
    status: n.data.label && n.data.label !== 'undefined' ? 'ok' : 'incomplete',
  }));

  const handleDragStart = (event, pa) => {
    event.dataTransfer.setData('application/reactflow', JSON.stringify(pa));
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div style={{
      display: 'flex',
      width: '100vw',
      height: '100vh',
      overflow: 'hidden',
      background: '#0f172a',
      color: '#e2e8f0',
    }}>
      {/* ===== LEFT SIDEBAR ===== */}
      <div className="cenec-sidebar" style={{
        width: 240,
        minWidth: 240,
        maxWidth: 240,
        flexShrink: 0,
        display: 'flex',
        flexDirection: 'column',
      }}>
        <div className="sidebar-brand">
          <img src="/cenec-logo.svg" alt="CENEC" className="sidebar-logo" />
          <small>Network Resource Grid</small>
        </div>

        <div style={{
          padding: '10px 16px',
          borderBottom: '1px solid var(--cenec-dark-surface, #1e2a4a)',
        }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#e2e8f0' }}>{projectName}</div>
          <div style={{ fontSize: 11, color: '#94a3b8', fontFamily: "'JetBrains Mono', monospace", marginTop: 2 }}>
            {versionName} · Rev {revLetter}
          </div>
        </div>

        <nav className="sidebar-nav" style={{ padding: '4px 0', borderBottom: '1px solid var(--cenec-dark-surface, #1e2a4a)' }}>
          <a href="#back" onClick={(e) => { e.preventDefault(); onBack(); }}>
            <span className="nav-icon">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><polyline points="15 18 9 12 15 6"/></svg>
            </span>
            Back to Project
          </a>
        </nav>

        <div style={{ flex: 1, overflowY: 'auto' }}>
          <div className="sidebar-section-title">Project Assets</div>
          {projectAssets.length === 0 && (
            <div style={{ padding: '12px 16px', fontSize: 11, color: '#94a3b8', fontStyle: 'italic' }}>
              No assets configured.
            </div>
          )}
          {CATEGORIES.map((cat) => {
            const items = groupedAssets[cat.id] || [];
            if (items.length === 0) return null;
            return (
              <div key={cat.id}>
                <div style={{
                  padding: '8px 16px 2px 16px',
                  fontSize: 10,
                  fontWeight: 600,
                  color: cat.color,
                  textTransform: 'uppercase',
                  letterSpacing: '0.5px',
                }}>
                  <span style={{ display: 'inline-block', width: 6, height: 6, borderRadius: '50%', background: cat.color, marginRight: 4 }} />
                  {cat.label}
                </div>
                {items.map((pa) => (
                  <div
                    key={pa.id}
                    draggable
                    onDragStart={(e) => handleDragStart(e, pa)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '6px 16px',
                      fontSize: 12,
                      color: '#e2e8f0',
                      cursor: 'grab',
                      borderLeft: '3px solid transparent',
                      transition: 'background 0.12s, color 0.12s, border-left-color 0.12s',
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.background = 'var(--cenec-dark-surface, #1e2a4a)';
                      e.currentTarget.style.color = cat.color;
                      e.currentTarget.style.borderLeftColor = cat.color;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = 'transparent';
                      e.currentTarget.style.color = '#e2e8f0';
                      e.currentTarget.style.borderLeftColor = 'transparent';
                    }}
                  >
                    <span style={{ display: 'flex', opacity: 0.9 }}>{getAssetIcon(pa.asset_def_id, cat.color)}</span>
                    <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {pa.name}
                    </span>
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>

      {/* ===== CENTER — CANVAS ===== */}
      <div style={{
        flex: 1,
        height: '100vh',
        position: 'relative',
        overflow: 'hidden',
      }}>
        <ReactFlow
          nodes={nodes}
          edges={edges}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          onInit={setReactFlowInstance}
          onDrop={onDrop}
          onDragOver={onDragOver}
          onNodeClick={onNodeClick}
          onNodeDoubleClick={onNodeDoubleClick}
          onPaneClick={onPaneClick}
          nodeTypes={nodeTypes}
          defaultEdgeOptions={defaultEdgeOptions}
          fitView
          snapToGrid
          snapGrid={[16, 16]}
          deleteKeyCode="Backspace"
          multiSelectionKeyCode="Shift"
          style={{ background: '#131c2e' }}
        >
          <Controls />
          <MiniMap
            style={{ background: '#1e293b' }}
            nodeColor={(n) => n.data?.color || '#4ecca3'}
            maskColor="rgba(15, 23, 42, 0.7)"
          />
          <Background
            variant={BackgroundVariant.Lines}
            gap={40}
            size={0.5}
            color="#253348"
          />
        </ReactFlow>
      </div>

      {/* ===== RIGHT PANEL — CANVAS ITEMS ===== */}
      <div style={{
        width: 220,
        minWidth: 220,
        maxWidth: 220,
        flexShrink: 0,
        background: 'var(--cenec-dark, #0d1525)',
        borderLeft: '1px solid var(--cenec-dark-surface, #1e2a4a)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}>
        <div style={{
          padding: '12px 14px',
          borderBottom: '1px solid var(--cenec-dark-surface, #1e2a4a)',
          fontSize: 12,
          fontWeight: 600,
          color: '#e2e8f0',
          fontFamily: "'Space Grotesk', sans-serif",
        }}>
          Canvas Items
          <span style={{ color: '#94a3b8', fontWeight: 400, marginLeft: 6, fontSize: 11 }}>{canvasItems.length}</span>
        </div>
        <div style={{ flex: 1, overflowY: 'auto', padding: '4px 0' }}>
          {canvasItems.length === 0 && (
            <div style={{ padding: '20px 14px', fontSize: 11, color: '#94a3b8', fontStyle: 'italic', textAlign: 'center' }}>
              Drag assets from the left onto the canvas.
            </div>
          )}
          {canvasItems.map((item) => (
            <div
              key={item.id}
              onClick={() => {
                const node = nodes.find((n) => n.id === item.id);
                if (node && reactFlowInstance) {
                  setSelectedNodeId(item.id);
                  reactFlowInstance.fitView({ nodes: [node], padding: 0.5, duration: 300 });
                }
              }}
              style={{
                padding: '5px 8px 5px 14px',
                fontSize: 11,
                display: 'flex',
                alignItems: 'center',
                gap: 6,
                cursor: 'pointer',
                background: item.selected ? '#1e2a4a' : 'transparent',
                borderLeft: item.selected ? `3px solid ${item.color}` : '3px solid transparent',
                transition: 'background 0.12s',
              }}
              onMouseEnter={(e) => { if (!item.selected) e.currentTarget.style.background = '#1e2a4a'; }}
              onMouseLeave={(e) => { if (!item.selected) e.currentTarget.style.background = 'transparent'; }}
            >
              <span style={{
                width: 8, height: 8, borderRadius: '50%',
                background: item.status === 'ok' ? '#4ecca3' : '#fbbf24',
                flexShrink: 0,
              }} />
              <span style={{ display: 'flex' }}>{getAssetIcon(item.assetType, item.color)}</span>
              <span style={{
                flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                color: item.selected ? '#e2e8f0' : '#94a3b8',
              }}>
                {item.label}
              </span>
              <span
                onClick={(e) => {
                  e.stopPropagation();
                  setNodes((nds) => nds.filter((n) => n.id !== item.id));
                  setEdges((eds) => eds.filter((ed) => ed.source !== item.id && ed.target !== item.id));
                  if (selectedNodeId === item.id) setSelectedNodeId(null);
                }}
                title="Remove from canvas"
                style={{
                  flexShrink: 0,
                  width: 16,
                  height: 16,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 3,
                  color: '#475569',
                  fontSize: 12,
                  cursor: 'pointer',
                  transition: 'color 0.12s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.color = '#dc2626'; }}
                onMouseLeave={(e) => { e.currentTarget.style.color = '#475569'; }}
              >
                ✕
              </span>
            </div>
          ))}
        </div>
        {canvasItems.length > 0 && (
          <div style={{
            padding: '8px 14px',
            borderTop: '1px solid var(--cenec-dark-surface, #1e2a4a)',
            fontSize: 10,
            color: '#94a3b8',
            fontFamily: "'JetBrains Mono', monospace",
          }}>
            {canvasItems.length} items · {canvasItems.filter((i) => i.status === 'ok').length} ● · {canvasItems.filter((i) => i.status !== 'ok').length} ◐
          </div>
        )}
      </div>
    </div>
  );
};

export default CanvasViewer;
